export interface EndLabelAnchor<TId extends string = string> {
  id: TId;
  /** Pixel y of the label's line end. */
  y: number;
}

export interface EndLabelBounds {
  top: number;
  bottom: number;
}

export const END_LABEL_MIN_GAP = 30;

/**
 * Vertical positions for the chips at the end of each chart line: top to bottom
 * in line order, at least `minGap` apart, and inside `bounds`. A chip only moves
 * as far as its own crowded cluster needs, so an isolated chip stays on its line.
 * When the chips cannot all fit, the top edge wins.
 */
export function layoutEndLabels<TId extends string>(
  anchors: ReadonlyArray<EndLabelAnchor<TId>>,
  bounds: EndLabelBounds,
  minGap = END_LABEL_MIN_GAP,
): EndLabelAnchor<TId>[] {
  const placed = [...anchors].sort((a, b) => a.y - b.y).map((anchor) => ({ ...anchor }));
  if (placed.length === 0) return placed;

  pushDown(placed, bounds.top, minGap);
  pushUp(placed, bounds.bottom, minGap);
  pushDown(placed, bounds.top, minGap);

  return placed;
}

function pushDown(placed: EndLabelAnchor[], top: number, minGap: number) {
  placed[0].y = Math.max(placed[0].y, top);
  for (let i = 1; i < placed.length; i++) {
    placed[i].y = Math.max(placed[i].y, placed[i - 1].y + minGap);
  }
}

function pushUp(placed: EndLabelAnchor[], bottom: number, minGap: number) {
  const last = placed.length - 1;
  placed[last].y = Math.min(placed[last].y, bottom);
  for (let i = last - 1; i >= 0; i--) {
    placed[i].y = Math.min(placed[i].y, placed[i + 1].y - minGap);
  }
}
